/**
 * FIP List Table
 *
 * Displays FIP records with optional edit action.
 */

import React, { useMemo, useState } from 'react';
import { Table, Button, SearchBar, type TableColumn } from 'equal-ds-ui';
import { PAGE_SIZE_OPTIONS } from '../../../constants/pagination.constants';
import type { FipListTableProps, FipRecord } from '../../../types/fip-list';

const formatIdentifiers = (identifiers: FipRecord['identifiers']): string => {
  if (Array.isArray(identifiers)) {
    return identifiers.join(', ');
  }

  if (typeof identifiers === 'object' && identifiers !== null) {
    return Object.values(identifiers).join(', ');
  }

  return identifiers ? String(identifiers) : '--';
};

const filterFipRecords = (
  records: FipRecord[],
  query: string,
  domain: string
): FipRecord[] => {
  const trimmedQuery = query.trim().toLowerCase();
  if (!trimmedQuery) {
    return records;
  }

  return records.filter((record) => {
    const fiTypesText = Array.isArray(record.fiTypes)
      ? record.fiTypes.join(', ').toLowerCase()
      : '';
    const identifiersText = formatIdentifiers(record.identifiers).toLowerCase();
    const statusLabel = record.status ? 'active' : 'inactive';

    switch (domain) {
      case 'name':
        return record.fipName?.toLowerCase().includes(trimmedQuery);
      case 'id':
        return record.fipId?.toLowerCase().includes(trimmedQuery);
      case 'fiType':
        return fiTypesText.includes(trimmedQuery);
      case 'identifier':
        return identifiersText.includes(trimmedQuery);
      case 'status':
        return statusLabel.includes(trimmedQuery);
      case 'all':
      default:
        return (
          record.fipName?.toLowerCase().includes(trimmedQuery) ||
          record.fipId?.toLowerCase().includes(trimmedQuery) ||
          fiTypesText.includes(trimmedQuery) ||
          identifiersText.includes(trimmedQuery) ||
          statusLabel.includes(trimmedQuery)
        );
    }
  });
};

export const FipListTable: React.FC<FipListTableProps> = ({
  data,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  isLoading,
  canEdit,
  isSuperAdmin,
  onEdit,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDomain, setSearchDomain] = useState('all');

  const domains = [
    { value: 'all', label: 'All Fields' },
    { value: 'name', label: 'FIP Name' },
    { value: 'id', label: 'FIP ID' },
    { value: 'fiType', label: 'FI Account Types' },
    { value: 'identifier', label: 'Identifiers' },
    { value: 'status', label: 'Status' },
  ];

  const filteredData = useMemo(
    () => filterFipRecords(data, searchQuery, searchDomain),
    [data, searchQuery, searchDomain]
  );

  const columns = useMemo<TableColumn<FipRecord>[]>(() => {
    const cols: TableColumn<FipRecord>[] = [
      {
        key: 'fipName',
        header: 'FIP Name',
        width: 220,
        render: (_value, row) => <span className="text-sm">{row.fipName}</span>,
      },
      {
        key: 'fipId',
        header: 'FIP ID',
        width: 160,
        render: (_value, row) => (
          <span className="text-sm font-mono">{row.fipId}</span>
        ),
      },
      {
        key: 'fiTypes',
        header: 'FI Account Types',
        width: 260,
        render: (_value, row) => (
          <span className="text-sm break-words">
            {Array.isArray(row.fiTypes) ? row.fiTypes.join(', ') : '--'}
          </span>
        ),
      },
      {
        key: 'identifiers',
        header: 'Identifiers',
        width: 220,
        render: (_value, row) => (
          <span className="text-sm break-words">
            {formatIdentifiers(row.identifiers)}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'FIP Status',
        width: 140,
        render: (_value, row) => {
          const active = Boolean(row.status);
          return (
            <span
              className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${
                active
                  ? 'bg-green-100 text-green-800 border border-green-200'
                  : 'bg-blue-100 text-blue-800 border border-blue-200'
              }`}
            >
              {active ? 'ACTIVE' : 'INACTIVE'}
            </span>
          );
        },
      },
    ];

    if (canEdit) {
      cols.push({
        key: 'actions',
        header: 'Actions',
        width: 140,
        align: 'center',
        render: (_value, row) => (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onEdit(row)}
            disabled={!isSuperAdmin}
          >
            Edit
          </Button>
        ),
      });
    }

    return cols;
  }, [canEdit, isSuperAdmin, onEdit]);

  return (
    <div className="flex-1 min-h-0 flex flex-col p-4 border border-border-default rounded-xl shadow-sm bg-white">
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1">
          <SearchBar
            variant="with-dropdown"
            domains={domains}
            value={searchQuery}
            onChange={setSearchQuery}
            selectedDomain={searchDomain}
            onDomainChange={setSearchDomain}
            placeholder="Search FIPs..."
          />
        </div>
      </div>

      <Table
        data={filteredData}
        columns={columns}
        loading={isLoading}
        pagination={{
          currentPage,
          pageSize,
          totalItems,
          onPageChange,
          onPageSizeChange,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
        columnManager={{
          enabled: true,
          allowHiding: false,
        }}
      />
    </div>
  );
};
